import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import { uploadData, getUrl, remove } from "aws-amplify/storage";

interface ProfilePhotoState {
  isModalOpen: boolean;
  selectedFile: File | null;
  previewImage: string | null;
  profilePhotoUrl: string | null;
  uploadProgress: number | undefined;
  successMessage: string | null;
  errorMessage: string | null;
}

interface ProfilePhotoContextType extends ProfilePhotoState {
  handleFileSelect: (file: File) => void;
  handleUpload: (file: File) => Promise<void>;
  handleDelete: () => Promise<void>;
  refreshProfilePhotoUrl: () => Promise<string | undefined>;
  setIsModalOpen: (isOpen: boolean) => void;
  setErrorMessage: (message: string | null) => void;
}

const ProfilePhotoContext = createContext<ProfilePhotoContextType | undefined>(
  undefined
);

export const useProfilePhoto = () => {
  const context = useContext(ProfilePhotoContext);
  if (context === undefined) {
    return {
      isModalOpen: false,
      selectedFile: null,
      previewImage: null,
      profilePhotoUrl: "",
      uploadProgress: undefined,
      successMessage: null,
      errorMessage: null,
      handleFileSelect: () => {},
      handleUpload: async () => {},
      handleDelete: async () => {},
      refreshProfilePhotoUrl: async () => undefined,
      setIsModalOpen: () => {},
      setErrorMessage: () => {},
    };
  }
  return context;
};

export const ProfilePhotoProvider: React.FC<{
  children: React.ReactNode;
  userData: any;
  updateUserData: any;
}> = ({ children, userData, updateUserData }) => {
  const mountedRef = useRef(false);
  const [ProfilePhotoState, setProfilePhotoState] = useState<ProfilePhotoState>(
    {
      isModalOpen: false,
      selectedFile: null,
      previewImage: null,
      profilePhotoUrl: "",
      uploadProgress: undefined,
      successMessage: null,
      errorMessage: null,
    }
  );

  const updateState = useCallback((newState: Partial<ProfilePhotoState>) => {
    if (mountedRef.current) {
      setProfilePhotoState((prevState) => ({ ...prevState, ...newState }));
    }
  }, []);

  const getProfilePhotoUrl = useCallback(async () => {
    if (
      userData?.profilePictureUrl &&
      userData.profilePictureUrl.trim() !== ""
    ) {
      try {
        const { url } = await getUrl({
          path: userData.profilePictureUrl,
          options: { expiresIn: 3600 },
        });

        const newUrl = url.toString();
        if (newUrl !== ProfilePhotoState.profilePhotoUrl) {
          updateState({ profilePhotoUrl: newUrl });
        }
        return newUrl;
      } catch (error) {
        console.error("Error fetching profile photo URL:", error);
        updateState({
          profilePhotoUrl: null,
          errorMessage: "Failed to load profile photo. Please try again later.",
        });
      }
    } else {
      updateState({ profilePhotoUrl: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.profilePictureUrl, ProfilePhotoState.profilePhotoUrl]);

  // Effect for initial mount and cleanup
  useEffect(() => {
    // console.log("Component mounted");
    mountedRef.current = true;

    return () => {
      // console.log("Component unmounting");
      mountedRef.current = false;
    };
  }, []);

  // Effect specifically for handling userData changes
  useEffect(() => {
    // console.log("userData changed:", userData);
    if (userData?.id) {
      getProfilePhotoUrl();
    }
  }, [userData, getProfilePhotoUrl]);

  // Effect for refresh interval
  useEffect(() => {
    if (!userData?.id) return;

    // console.log("Setting up refresh interval");
    const intervalId = setInterval(getProfilePhotoUrl, 55 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [userData?.id, getProfilePhotoUrl]);

  const refreshProfilePhotoUrl = useCallback(async () => {
    return await getProfilePhotoUrl();
  }, [getProfilePhotoUrl]);

  const handleFileSelect = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      updateState({
        selectedFile: file,
        previewImage: reader.result as string,
      });
    };
    reader.readAsDataURL(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = useCallback(
    async (file: File) => {
      try {
        const result = await uploadData({
          path: ({ identityId }) =>
            `protected/${identityId}/profilePhoto/${file.name}`,
          data: file,
          options: {
            onProgress: ({ transferredBytes, totalBytes }) => {
              if (totalBytes) {
                const progress = (transferredBytes / totalBytes) * 100;
                updateState({ uploadProgress: progress });
              }
            },
          },
        }).result;

        const existingFiles = userData.profilePictureUrl;

        if (existingFiles && existingFiles !== result.path) {
          await remove({ path: existingFiles });
        }

        const success = await updateUserData({
          id: userData?.id,
          profilePictureUrl: result.path,
        });

        if (success) {
          await getProfilePhotoUrl();
          updateState({
            profilePhotoUrl: result.path,
            successMessage: "Profile photo updated successfully.",
            errorMessage: null,
            selectedFile: null,
            previewImage: null,
            uploadProgress: undefined,
          });

          setTimeout(() => {
            updateState({ isModalOpen: false, successMessage: null });
          }, 1000);
        } else {
          updateState({
            errorMessage: "Failed to update profile photo. Please try again.",
            successMessage: null,
          });
        }
      } catch (error) {
        console.error("Upload error:", error);
        updateState({
          errorMessage: "An error occurred while updating.",
          successMessage: null,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userData, updateUserData, getProfilePhotoUrl]
  );
  const handleDelete = useCallback(async () => {
    try {
      if (userData?.profilePictureUrl) {
        await remove({ path: userData.profilePictureUrl });

        const success = await updateUserData({
          id: userData.id,
          profilePictureUrl: "",
        });

        if (success) {
          updateState({
            profilePhotoUrl: null,
            successMessage: "Profile photo deleted successfully.",
            errorMessage: null,
          });

          setTimeout(() => {
            updateState({ successMessage: null });
          }, 100);
        } else {
          updateState({
            errorMessage: "Failed to update user data after deleting photo.",
            successMessage: null,
          });
        }
      } else {
        updateState({
          errorMessage: "No profile photo found to delete.",
          successMessage: null,
        });
      }
    } catch {
      updateState({
        errorMessage: "An error occurred while deleting.",
        successMessage: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, updateUserData]);

  const value: ProfilePhotoContextType = {
    ...ProfilePhotoState,
    handleFileSelect,
    handleUpload,
    handleDelete,
    refreshProfilePhotoUrl,
    setIsModalOpen: (isOpen: boolean) => updateState({ isModalOpen: isOpen }),
    setErrorMessage: (message: string | null) =>
      updateState({ errorMessage: message }),
  };

  return (
    <ProfilePhotoContext.Provider value={value}>
      {children}
    </ProfilePhotoContext.Provider>
  );
};

export default ProfilePhotoProvider;
